import type { Vehicle } from '@limo/database'
import {
  Briefcase,
  Car,
  DollarSign,
  EyeOff,
  MapPin,
  MoreHorizontal,
  Pencil,
  Tag,
  Trash2,
  Users,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { VEHICLE_TYPE_LABELS } from '@/schemas/vehicle'
import {
  describeVehicleQuoteMode,
  isHiddenByPricing,
  summarizeVehiclePricing,
  type VehiclePricingPreviewRow,
} from '@/lib/vehicles/pricing-preview'

export type VehicleWithPricingRows = Vehicle & {
  vehicle_pricing?: VehiclePricingPreviewRow[] | null
}

interface VehicleCardProps {
  vehicle: VehicleWithPricingRows
  /** The account's `booking_custom_quotes_enabled` switch. */
  customQuotesEnabled: boolean
  onOpen: () => void
  onEdit: () => void
  onDelete: () => void
}

/**
 * One vehicle in the fleet grid.
 *
 * Shows enough to answer "what does this cost and how is it sold?" without
 * opening the vehicle: the enabled pricing rows, whether it is quote-only, and
 * the mileage past which it switches to a manual quote.
 */
export function VehicleCard({
  vehicle,
  customQuotesEnabled,
  onOpen,
  onEdit,
  onDelete,
}: VehicleCardProps) {
  const preview = summarizeVehiclePricing(vehicle.vehicle_pricing)
  const quoteMode = describeVehicleQuoteMode({ customQuotesEnabled, vehicle })
  const hiddenByPricing = isHiddenByPricing({ preview, quoteMode })

  const quoteOnlyInEffect = quoteMode.kind === 'quote_only' && quoteMode.effective

  return (
    <Card
      className="cursor-pointer overflow-hidden transition-shadow hover:shadow-md"
      onClick={onOpen}
    >
      <div className="relative aspect-video bg-muted">
        {vehicle.image_url ? (
          <img
            src={vehicle.image_url}
            alt={vehicle.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Car className="h-12 w-12 text-muted-foreground/50" />
          </div>
        )}
        <div className="absolute right-2 top-2" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="secondary"
                size="icon"
                className="h-8 w-8 bg-background/80 backdrop-blur-sm"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem className="text-destructive" onClick={onDelete}>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold">{vehicle.name}</h3>
          {!vehicle.is_active && <Badge variant="secondary">Inactive</Badge>}
        </div>

        <div className="mt-2 flex items-center gap-3 text-sm text-muted-foreground">
          <Badge variant="outline">
            {VEHICLE_TYPE_LABELS[
              vehicle.vehicle_type as keyof typeof VEHICLE_TYPE_LABELS
            ] || vehicle.vehicle_type}
          </Badge>
          <div className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            <span>{vehicle.passenger_capacity}</span>
          </div>
          {vehicle.luggage_capacity !== null && (
            <div className="flex items-center gap-1">
              <Briefcase className="h-3.5 w-3.5" />
              <span>{vehicle.luggage_capacity}</span>
            </div>
          )}
        </div>

        <div className="mt-3 space-y-1.5 border-t pt-3">
          {quoteOnlyInEffect ? (
            <div className="flex items-center gap-1.5 text-sm">
              <Tag className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="font-medium">Quote only</span>
              <span className="text-muted-foreground">— you set each price</span>
            </div>
          ) : null}

          {preview.lines.map((line) => (
            <div
              key={line.pricingType}
              className={
                quoteOnlyInEffect
                  ? 'flex items-start gap-1.5 text-sm text-muted-foreground/70'
                  : 'flex items-start gap-1.5 text-sm text-muted-foreground'
              }
            >
              <DollarSign className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                <span className="font-medium text-foreground/80">{line.label}</span>
                {' · '}
                {line.parts.join(' · ')}
              </span>
            </div>
          ))}

          {hiddenByPricing && (
            // No app-level TooltipProvider in this app — each usage brings its own,
            // matching LeadAgeIndicator and the other tooltip call sites.
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5 text-sm text-amber-600">
                    <EyeOff className="h-3.5 w-3.5 shrink-0" />
                    <span>No active pricing</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  This vehicle will not appear in the booking flow until at least one
                  pricing type is enabled.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {quoteMode.kind === 'distance' && (
            <div
              className={
                quoteMode.effective
                  ? 'flex items-center gap-1.5 text-sm text-muted-foreground'
                  : 'flex items-center gap-1.5 text-sm text-amber-600'
              }
            >
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span>
                Quote over {quoteMode.miles} mi
                {!quoteMode.effective && ' — custom quotes off'}
              </span>
            </div>
          )}

          {quoteMode.kind === 'quote_only' && !quoteMode.effective && (
            <div className="flex items-center gap-1.5 text-sm text-amber-600">
              <Tag className="h-3.5 w-3.5 shrink-0" />
              <span>Quote only — custom quotes off</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
